import React, {useContext, useState} from 'react';
import {BoShowContext} from "../context/bo-show-context.jsx";
import Button from "../../../../../components/button/button.jsx";
import bosService from "../../../../../services/web/bos-service.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import projectUtils from "../../../../../utils/project-utils.js";
import {useLoaderData} from "react-router";
import {Modal} from "../../../../../components/modal/modal.jsx";
import {useQuery} from "@tanstack/react-query";
import Alert from "../../../../../components/alert/alert.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import InputTextarea from "../../../../../components/input/input-textarea.jsx";
import {toast} from "react-toastify";
import destroyUtils from "../../../../../utils/destroy-utils.js";

const BoShowButtons = () => {
    const context = useContext(BoShowContext);
    const {id, idRaw, isSync, dataFetch, lastSync, syncId, setIsLoading, refetch} = context;

    const loaderData = useLoaderData();
    const boName = loaderData?.bo_name;
    const navigate = navigateHook();

    const [modalRejectedObservation, setModalRejectedObservation] = useState(false);
    const [modalDiff, setModalDiff] = useState(false);
    const [modalReject, setModalReject] = useState(false);

    const [reject, setReject] = useState({rejectedJustification: "", rejectObs: ""});

    const boGcmItems = dataFetch?.bogcm;
    const actionsItems = boGcmItems.actions;

    const {data: boDifferences} = useQuery({
        queryFn: () => bosService.showBoDiff({params: {id}}),
        queryKey: ['boDifferences'],
        enabled: !!idRaw && actionsItems.viewDiff
    });

    const {data: rejectedJustifications} = useQuery({
        queryFn: () => bosService.rejectedJustifications(),
        queryKey: ['rejectedJustifications'],
    });

    const visibleDifferences = boDifferences?.filter(diff =>
        diff.value?.length || diff.removedAttachments > 0 || diff.addedAttachments > 0
    ) ?? [];

    const editButton = () => {
        if (!boGcmItems?.actions?.edit) return null;

        const editClick = () => {
            if (lastSync) {
                navigate(`/bo/form/${idRaw}/${syncId}`)
            } else if (!syncId) {
                navigate(`/bo/form/${idRaw}`)
            } else {
                projectUtils.swalMessage(
                    "warning",
                    boName + " " + id,
                    `Deseja continuar a edição do ${boName} a partir dessa versão?`,
                    "Editar",
                    true,
                    "Cancelar",
                    ({isConfirmed}) => {
                        if (isConfirmed) {
                            navigate(`/bo/form/${idRaw}/${syncId}`)
                        }
                    }
                )
            }
        }

        const persistBo = async () => {
            const confirmed = await destroyUtils.keywordDestroy(
                `Deseja enviar o ${boName} ${id} para aprovação?`,
                "Confirmar",
            );

            if (!confirmed) return;

            const formDataPersist = new FormData();
            formDataPersist.append('syncId', syncId);
            formDataPersist.append('deviceTimestamp', Date.now());

            setIsLoading(true)
            bosService.persist(formDataPersist)
                .then(() => {
                    projectUtils.success(true, "Enviado com sucesso")
                    navigate("/bos")
                })
                .catch(error => {
                    console.error(error);
                    projectUtils.swalErrorMessage('', 'Não foi possível enviar.', 'Ok');
                })
                .finally(() => setIsLoading(false))
        }

        return (
            <>
                <Button className={"justify-self-center"} onClick={editClick}>
                    EDITAR
                </Button>

                {
                    boGcmItems.bogcm.status === 'DRAFT' && lastSync &&
                    <Button
                        className={"justify-self-center"}
                        onClick={persistBo}
                        actionFeedbackType="save"
                        loadingText="Enviando..."
                        successText="Enviado com sucesso"
                    >
                        ENVIAR PARA APROVAÇÃO
                    </Button>
                }
            </>
        )
    }

    const actionButton = () => {
        if (
            !actionsItems?.viewRejectedObservation &&
            !actionsItems?.viewDiff &&
            !actionsItems?.reject &&
            !actionsItems?.review &&
            !actionsItems?.approve
        ) {
            return null;
        }

        const showRejectedObservation =
            actionsItems.viewRejectedObservation &&
            boGcmItems.rejects?.[0];

        const showDiff =
            actionsItems.viewDiff &&
            boDifferences &&
            boDifferences.length > 0;

        const showReject = actionsItems.reject;
        const showReview = actionsItems.review;
        const showApprove = actionsItems.approve;

        return (
            <>
                <hr className={"opacity-25 text-[#676a6c]"}/>
                <h2 className={"text-center"}>Avaliação</h2>
                <div className="flex justify-center gap-2">
                    {showRejectedObservation && (
                        <Button onClick={() => setModalRejectedObservation(true)}>
                            MOTIVO DA REPROVAÇÃO
                        </Button>
                    )}

                    {showDiff && (
                        <Button onClick={() => setModalDiff(true)}>
                            ALTERAÇÕES
                        </Button>
                    )}

                    {showReject && (
                        <Button target="danger" onClick={() => setModalReject(true)}>
                            REPROVAR
                        </Button>
                    )}

                    {showReview && (
                        <Button
                            onClick={() => review()}
                            actionFeedbackType="save"
                            deferActionFeedback
                            loadingText="Revisando..."
                            successText="Revisado com sucesso"
                        >
                            REVISAR
                        </Button>
                    )}

                    {showApprove && (
                        <Button
                            onClick={() => approvalAction()}
                            actionFeedbackType="save"
                            deferActionFeedback
                            loadingText="Aprovando..."
                            successText="Aprovado com sucesso"
                        >
                            APROVAR
                        </Button>
                    )}
                </div>
            </>
        );
    }

    const review = () => {
        projectUtils.swalMessage(
            "warning",
            boName + " " + id,
            `Revisar o ${boName}?`,
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (isConfirmed) {
                    approval('REVIEW')
                }
            }
        )
    }

    const approvalAction = () => {
        projectUtils.swalMessage(
            "warning",
            boName + " " + id,
            `Aprovar o ${boName}?`,
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (isConfirmed) {
                    approval('APPROVE')
                }
            }
        )
    }

    const rejectAction = () => {
        if (!reject.rejectedJustification){
            projectUtils.swalErrorMessage("", "O campo \"Motivo\" é obrigatório.");
            return;
        }

        approval('REJECT', reject.rejectObs, reject.rejectedJustification);
    }

    const approval = (action = '', obs = '', rejectedJustification = '') => {
        const body = {
            bogcmId: id,
            action: action,
        };

        if (obs) {
            body.observation = obs;
        }

        if (rejectedJustification) {
            body.rejectedJustification = rejectedJustification;
        }

        setIsLoading(true)
        bosService.approval(body)
            .then(() => {
                let message;
                if (action === 'REJECT') {
                    message = 'Reprovado com sucesso';
                } else if (action === 'REVIEW') {
                    message = 'Revisado com sucesso';
                } else if (action === 'APPROVE') {
                    message = 'Aprovado com sucesso';
                }

                setModalReject(false);

                toast.success(message)
                refetch()
            })
            .catch(error => {
                console.error(error);

                let message = '';
                if(action === 'REJECT'){
                    message = 'Erro ao reprovar o ' + boName;
                } else if(action === 'REVIEW'){
                    message = 'Erro ao revisar o ' + boName;
                } else if(action === 'APPROVE'){
                    message = 'Erro ao aprovar o ' + boName;
                }

                if (error.response.status !== 400) {
                    const errorMessage = error.response.data.message;
                    if (errorMessage) {
                        if ('bogcm.already.approved') {
                            message = boName + ' já aprovado';
                        } else if ('bogcm.already.rejected') {
                            message = boName + ' já reprovado';
                        } else if ('bogcm.already.reviewed') {
                            message = boName + ' já revisado';
                        }
                    }
                }

                if (error.response.data.message !== '') {
                    message = error.response.data.message;

                    if (message === "no.observation" || message === "no.rejectedJustification") {
                        message = "Campo observação não pode ser vazio.";
                    }
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false))
    }

    return (
        <>
            <div className={"print:hidden !space-y-2"}>
                {
                    isSync &&
                    <>
                        {
                            !boGcmItems.bogcm.data && editButton()
                        }
                    </>
                }

                {
                    !isSync &&
                    <>
                        {editButton()}
                        {actionButton()}
                    </>
                }
            </div>

            <Modal open={modalRejectedObservation} setOpen={setModalRejectedObservation} title={`Motivos de reprovação do ${boName} ${id}`} className={"w-6/12"} overflow={true}>
                <Modal.Body className={"overflow-auto max-h-[calc(100vh-230px)]"}>
                    {
                        boGcmItems?.rejects?.map((item, index) => (
                            <React.Fragment key={"reject-" + index}>
                                {index !== 0 && <hr className={"opacity-25 text-[#676a6c]"}/>}
                                <section className={"space-y-2"}>
                                    <p>Reprovado por: <strong>{item.userName}</strong> em <strong>{item.dateTime}</strong></p>
                                    <p>Motivo: <span className={"info"}>{item.justification}</span></p>
                                    <p>Observação: <span className={"info"}>{item.observation}</span></p>
                                </section>
                            </React.Fragment>
                        ))
                    }
                </Modal.Body>
            </Modal>

            <Modal open={modalDiff} setOpen={setModalDiff} title={`Alterações no ${boName} ${id}`} className={"w-6/12"} overflow={true}>
                <Modal.Body className={"overflow-auto max-h-[calc(100vh-230px)]"}>
                    {
                        visibleDifferences.length === 0 ? (
                            <p className={"text-center text-[14px]!"}>Nenhuma alteração foi feita</p>
                        ) : visibleDifferences.map((diff, index) => (
                            <React.Fragment key={"diff-" + index}>
                                {index !== 0 && <hr className={"opacity-25 text-[#676a6c]"}/>}
                                <article className={"space-y-4"}>
                                    <h3 className={"font-semibold"}>{diff.group}</h3>
                                    {
                                        diff.value?.map((value, index) => (
                                            <section key={"diff-value-" + index}>
                                                {
                                                    diff.subtitle &&
                                                    <p className={"font-bold"}>{value.subtitle}</p>
                                                }

                                                <p>Campo: <b>{value.formattedKey}</b></p>
                                                <p>Antigo: <b>{value.oldValue ?? ""}</b></p>
                                                <p>Novo: <b>{value.newValue ?? ""}</b></p>
                                            </section>
                                        ))
                                    }
                                    {
                                        diff.removedAttachments > 0 &&
                                        <p>{diff.removedAttachments} {diff.removedAttachments > 1 ? "anexos foram removidos" : " anexo foi removido"}</p>
                                    }
                                    {
                                        diff.addedAttachments > 0 &&
                                        <p>{diff.addedAttachments} {diff.addedAttachments > 1 ? "anexos foram adicionados" : " anexo foi adicionado"}</p>
                                    }
                                </article>
                            </React.Fragment>
                        ))
                    }
                </Modal.Body>
            </Modal>

            <Modal open={modalReject} setOpen={setModalReject} title={`Reprovar o ${boName} ${id}?`} className={"w-6/12"} overflow={true}>
                <Modal.Body className={"space-y-4"}>
                    {
                        actionsItems?.rejectApproved &&
                        <Alert target={"danger"} className={"text-center"}>
                            Reprovar um {boName} já aprovado pode quebrar cadeia de custódia.<br/>
                            Deseja continuar mesmo assim?
                        </Alert>
                    }

                    <SelectComponent
                        name="related_agent"
                        value={reject.rejectedJustification}
                        options={rejectedJustifications}
                        label={"Motivo *"}
                        onChange={(value) => setReject(prev => ({...prev, rejectedJustification: value?.value}))}
                    />

                    <InputTextarea
                        id={"rejectObs"}
                        value={reject.rejectObs}
                        label={"Observação"}
                        textAreaClass={"h-[80px]"}
                        onChange={({target: {value}}) => setReject(prev => ({...prev, rejectObs: value}))}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={rejectAction}
                        actionFeedbackType="save"
                        loadingText="Reprovando..."
                        successText="Reprovado com sucesso"
                    >
                        Confirmar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default BoShowButtons;